import {
  CapitalActionEntryType,
  DrawdownSourcePriority,
} from 'further-types/lp/capital-action';
import { CalendarDay } from '../../../utils';
import { z } from 'zod';
import { isMongoId } from '../../helpers';
import { AllocationRule } from 'further-types/lp/allocations';
import { round, sumBy } from 'lodash';
import { TransactionOperation } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import {
  ActionStatus,
  ActionTransactionSubTypes,
} from 'further-types/lp/action';

export const CapitalActionEntries = z
  .array(
    z
      .object({
        id: z
          .string()
          .refine(isMongoId({ optional: true }))
          .optional(),
        entryType: z.nativeEnum(CapitalActionEntryType),
        actions: z.array(
          z.object({
            id: z.string().refine(isMongoId()),
            capitalBreakdown: z.array(
              z.object({
                commitmentId: z.string().refine(isMongoId()),
                amountLcy: z.number(),
                fundsCalled: z
                  .object({
                    fromCommitmentLcy: z.number(),
                    fromRecallableDistributionLcy: z.number(),
                  })
                  .optional(),
              }),
            ),
          }),
        ),
        totalAmountLcy: z.number(),
        allocationRule: z.nativeEnum(AllocationRule).optional(),
        drawdownSourcePriority: z.nativeEnum(DrawdownSourcePriority).optional(),
        capitalBreakdown: z
          .array(
            z
              .object({
                commitmentId: z.string().refine(isMongoId()),
                amountLcy: z.number(),
                fundsCalled: z
                  .object({
                    fromCommitmentLcy: z.number(),
                    fromRecallableDistributionLcy: z.number(),
                  })
                  .optional(),
                fundsDistributed: z
                  .object({
                    subjectToCarry: z.number(),
                    outsideOfCarry: z.number(),
                    recallableOutsideOfCarry: z.number(),
                    recallableSubjectToCarry: z.number(),
                  })
                  .optional()
                  .superRefine((funds, ctx) => {
                    if (!funds) {
                      return;
                    }
                    if (funds.recallableOutsideOfCarry > funds.outsideOfCarry) {
                      ctx.addIssue({
                        path: ['recallableOutsideOfCarry'],
                        code: z.ZodIssueCode.custom,
                        message:
                          'Recallable outside of carry cannot be greater than the outside of carry.',
                      });
                    }
                    if (funds.recallableSubjectToCarry > funds.subjectToCarry) {
                      ctx.addIssue({
                        path: ['recallableSubjectToCarry'],
                        code: z.ZodIssueCode.custom,
                        message:
                          'Recallable subject to carry cannot be greater than the subject to carry.',
                      });
                    }
                  }),
              })
              .superRefine((breakdown, ctx) => {
                if (
                  breakdown.fundsCalled &&
                  round(
                    breakdown.fundsCalled.fromCommitmentLcy +
                      breakdown.fundsCalled.fromRecallableDistributionLcy,
                    2,
                  ) !== round(breakdown.amountLcy, 2)
                ) {
                  ctx.addIssue({
                    path: ['fundsCalled'],
                    code: z.ZodIssueCode.custom,
                    message:
                      'Funds called must be equal to the sum of from commitment and from recallable distribution.',
                  });
                }
              }),
          )
          .optional(),
      })
      .superRefine((data, ctx) => {
        if (data.entryType === CapitalActionEntryType.GeneralCapitalCall) {
          if (
            data.totalAmountLcy !==
            round(sumBy(data.capitalBreakdown, 'amountLcy'), 2)
          ) {
            ctx.addIssue({
              path: ['totalAmountLcy'],
              code: z.ZodIssueCode.custom,
              message:
                'Total funds to call must be equal to the sum of capital allocations.',
            });
          }
        } else if (
          data.entryType === CapitalActionEntryType.AdvanceSubscriptionPayment
        ) {
          // For advance subscription payment, totalAmountLcy should equal the sum of capitalBreakdown
          if (
            data.totalAmountLcy !==
            round(sumBy(data.capitalBreakdown, 'amountLcy'), 2)
          ) {
            ctx.addIssue({
              path: ['totalAmountLcy'],
              code: z.ZodIssueCode.custom,
              message:
                'Total advance subscription payment must be equal to the sum of capital allocations.',
            });
          }
        } else if (
          [
            CapitalActionEntryType.Expense,
            CapitalActionEntryType.Investment,
          ].includes(data.entryType)
        ) {
          if (!data.actions.length) {
            ctx.addIssue({
              path: ['actions'],
              code: z.ZodIssueCode.custom,
              message: 'At least one action is required.',
            });
          }
        }
      }),
  )
  .superRefine((entries, ctx) => {
    const capitalDistributionEntries = entries.filter(
      (entry) => entry.entryType === CapitalActionEntryType.CapitalDistribution,
    );

    if (capitalDistributionEntries.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Only one capital distribution entry is allowed.',
      });
    } else if (
      capitalDistributionEntries.length === 1 &&
      entries[entries.length - 1].entryType !==
        CapitalActionEntryType.CapitalDistribution
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Capital distribution must be the last entry.',
      });
    }
  });

const BaseCapitalAction = z.object({
  vehicleId: z.string().refine(isMongoId()),
  name: z.string().min(1, 'Capital action name is required.'),
  ledgerDate: z.instanceof(CalendarDay, {
    message: 'Capital action date is required.',
  }),
  actionDueDate: z.instanceof(CalendarDay, {
    message: 'Capital action due date is required.',
  }),
});

const validateActionDueDateIsOnOrAfterLedgerDate = (
  data: z.infer<typeof BaseCapitalAction>,
  ctx: z.RefinementCtx,
) => {
  if (data.actionDueDate.isBefore(data.ledgerDate)) {
    ctx.addIssue({
      path: ['actionDueDate'],
      code: z.ZodIssueCode.custom,
      message:
        'Capital action due date must be same or after the capital action date.',
    });
  }
};

export const CreateCapitalAction = BaseCapitalAction.superRefine(
  validateActionDueDateIsOnOrAfterLedgerDate,
);

export const UpdateCapitalAction = BaseCapitalAction.extend({
  id: z.string().refine(isMongoId()),
  status: z.nativeEnum(ActionStatus),
  entries: CapitalActionEntries.refine((entries) => !!entries.length, {
    message: 'At least one entry is required.',
  }).optional(),
  transactions: z
    .array(
      z.object({
        commitmentId: z.string().refine(isMongoId()),
        investorName: z.string(),
        transactions: z.array(
          z.object({
            id: z
              .string()
              .refine(isMongoId({ optional: true }))
              .optional(),
            transactionTypeId: z.string().refine(isMongoId()),
            amountExVatLcy: z.number(),
            amountIncVatLcy: z.number(),
            amountExVatTcy: z.number(),
            amountIncVatTcy: z.number(),
            operationType: z.nativeEnum(TransactionOperation),
            transactionSubType: z.nativeEnum(ActionTransactionSubTypes),
            transactionCurrency: z.nativeEnum(CurrencyCode),
            effectiveCapitalDate: z.instanceof(CalendarDay).optional(),
          }),
        ),
      }),
    )
    .optional(),
}).superRefine((data, ctx) => {
  validateActionDueDateIsOnOrAfterLedgerDate(data, ctx);

  if (data.status === ActionStatus.Approved) {
    if (!data.entries?.length) {
      ctx.addIssue({
        path: ['entries'],
        code: z.ZodIssueCode.custom,
        message: 'Entries are required to approve a capital action.',
      });
    }
  }
});

export const GetTotalCapitalContributionUsed = z.object({
  entries: z.array(
    z.object({
      entryType: z.nativeEnum(CapitalActionEntryType),
      actions: z
        .array(
          z.object({
            id: z.string().refine(isMongoId()),
            capitalBreakdown: z.array(
              z.object({
                commitmentId: z.string().refine(isMongoId()),
                amountLcy: z.number(),
                fundsCalled: z
                  .object({
                    fromCommitmentLcy: z.number(),
                    fromRecallableDistributionLcy: z.number(),
                  })
                  .optional(),
              }),
            ),
          }),
        )
        .optional(),
      capitalBreakdown: z
        .array(
          z.object({
            commitmentId: z.string().refine(isMongoId()),
            amountLcy: z.number(),
            fundsCalled: z
              .object({
                fromCommitmentLcy: z.number(),
                fromRecallableDistributionLcy: z.number(),
              })
              .optional(),
            fundsDistributed: z
              .object({
                subjectToCarry: z.number(),
                outsideOfCarry: z.number(),
                recallableOutsideOfCarry: z.number(),
                recallableSubjectToCarry: z.number(),
              })
              .optional(),
          }),
        )
        .optional(),
    }),
  ),
});

export const ValidateCalledCapital = z.object({
  actionId: z
    .string()
    .refine(isMongoId({ optional: true }))
    .optional(),
  allocationRule: z.nativeEnum(AllocationRule),
  drawdownSourcePriority: z.nativeEnum(DrawdownSourcePriority),
  capitalCalledLcy: z.number(),
  excludedCommitmentIds: z.array(z.string().refine(isMongoId())),
  recallableDistributionCalledInOtherEntries: z.array(
    z.object({
      commitmentId: z.string().refine(isMongoId()),
      calledAmountLcy: z.number(),
    }),
  ),
  existingBreakdown: z
    .array(
      z.object({
        commitmentId: z.string().refine(isMongoId()),
        fundsCalled: z
          .object({
            fromCommitmentLcy: z.number(),
            fromRecallableDistributionLcy: z.number(),
          })
          .optional(),
      }),
    )
    .optional(),
});
